import * as React from 'react';
import { Plus, Trash2, Key, Copy, Check } from 'lucide-react';
import { cn } from '../lib/utils.js';
import type { ApiKey, ApiKeyWithKey, ApiKeyPermission, App } from '../lib/api.js';
import { Button } from './ui/button.js';
import { Input } from './ui/input.js';
import { Select } from './ui/select.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card.js';
import { Badge } from './ui/badge.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog.js';

const PERMISSION_OPTIONS: Array<{ value: ApiKeyPermission; label: string }> = [
  { value: 'write', label: 'Write' },
  { value: 'read', label: 'Read' },
  { value: 'admin', label: 'Admin' },
];

export interface ApiKeyManagerProps {
  /** Array of API keys to display */
  apiKeys: ApiKey[];
  /** Available apps for selection */
  apps: App[];
  /** Currently selected app ID */
  selectedAppId?: string | null;
  /** Callback to select an app */
  onSelectApp?: (appId: string | null) => void;
  /** Callback when a new API key is created */
  onCreateApiKey: (appId: string, permissions: ApiKeyPermission, rateLimit: number) => Promise<ApiKeyWithKey>;
  /** Callback when an API key is deleted */
  onDeleteApiKey: (keyId: string) => Promise<void>;
  /** Whether data is loading */
  isLoading?: boolean;
  /** Error message to display */
  error?: string | null;
  /** Additional class name */
  className?: string;
}

/**
 * ApiKeyManager provides an interface for managing API keys.
 */
export function ApiKeyManager({
  apiKeys,
  apps,
  selectedAppId,
  onSelectApp,
  onCreateApiKey,
  onDeleteApiKey,
  isLoading = false,
  error = null,
  className,
}: ApiKeyManagerProps) {
  const [showCreateDialog, setShowCreateDialog] = React.useState(false);
  const [newKeyAppId, setNewKeyAppId] = React.useState(selectedAppId || '');
  const [newKeyPermissions, setNewKeyPermissions] = React.useState<ApiKeyPermission>('write');
  const [newKeyRateLimit, setNewKeyRateLimit] = React.useState('1000');
  const [isCreating, setIsCreating] = React.useState(false);
  const [createdKey, setCreatedKey] = React.useState<ApiKeyWithKey | null>(null);
  const [deleteKeyId, setDeleteKeyId] = React.useState<string | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [createError, setCreateError] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);

  // Update selected app when prop changes
  React.useEffect(() => {
    if (selectedAppId) {
      setNewKeyAppId(selectedAppId);
    }
  }, [selectedAppId]);

  // Reset form when dialog opens
  const openCreateDialog = () => {
    setNewKeyAppId(selectedAppId || apps[0]?.id || '');
    setNewKeyPermissions('write');
    setNewKeyRateLimit('1000');
    setCreateError(null);
    setShowCreateDialog(true);
  };

  const keyToDelete = apiKeys.find((key) => key.id === deleteKeyId);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError(null);

    if (!newKeyAppId) {
      setCreateError('Please select an app');
      return;
    }

    const rateLimit = parseInt(newKeyRateLimit, 10);
    if (isNaN(rateLimit) || rateLimit < 1 || rateLimit > 100000) {
      setCreateError('Rate limit must be between 1 and 100000');
      return;
    }

    setIsCreating(true);
    try {
      const key = await onCreateApiKey(newKeyAppId, newKeyPermissions, rateLimit);
      setShowCreateDialog(false);
      setCreatedKey(key);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create API key');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteKeyId) return;

    setIsDeleting(true);
    try {
      await onDeleteApiKey(deleteKeyId);
      setDeleteKeyId(null);
    } catch {
      // Error should be handled by parent
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCopyKey = async () => {
    if (!createdKey?.key) return;

    try {
      await navigator.clipboard.writeText(createdKey.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API might not be available
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getPermissionBadgeVariant = (permission: ApiKeyPermission) => {
    switch (permission) {
      case 'admin':
        return 'destructive';
      case 'read':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const appOptions = apps.map((app) => ({
    value: app.id,
    label: app.name,
  }));

  return (
    <Card className={cn('overflow-hidden', className)}>
      <CardHeader className="border-b border-[var(--color-border)] bg-[var(--color-secondary)]/30">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-500/10">
            <Key className="h-5 w-5 text-purple-400" />
          </div>
          <div>
            <CardTitle className="text-[var(--color-foreground)]">API Keys</CardTitle>
            <CardDescription className="text-[var(--color-muted-foreground)]">
              Manage API keys for your applications. Keys are shown only once when created.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 p-6">
        {/* App Filter */}
        {onSelectApp && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-[var(--color-muted-foreground)]">Filter by app:</span>
            <Select
              value={selectedAppId || ''}
              onChange={(e) => onSelectApp(e.target.value || null)}
              options={[{ value: '', label: 'All apps' }, ...appOptions]}
              className="w-48"
            />
          </div>
        )}

        {/* Create API Key Button */}
        <Button onClick={openCreateDialog} disabled={apps.length === 0}>
          <Plus className="h-4 w-4" />
          Create API Key
        </Button>

        {apps.length === 0 && (
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Create an app first before adding API keys.
          </p>
        )}

        {error && (
          <div className="flex items-center gap-2 rounded-lg border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 px-4 py-3 text-sm text-[var(--color-destructive)]">
            {error}
          </div>
        )}

        {/* API Keys Table */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-3 text-[var(--color-muted-foreground)]">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-[var(--color-primary)] border-t-transparent" />
              <span className="text-sm">Loading API keys...</span>
            </div>
          </div>
        ) : apiKeys.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-secondary)]">
              <Key className="h-6 w-6 text-[var(--color-muted-foreground)]" />
            </div>
            <p className="mt-4 text-[var(--color-muted-foreground)]">
              {apps.length === 0
                ? 'Create an app first to add API keys.'
                : 'No API keys yet. Create your first key above.'}
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-[var(--color-border)]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Key Prefix</TableHead>
                  <TableHead>App</TableHead>
                  <TableHead>Permissions</TableHead>
                  <TableHead>Rate Limit</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-16" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {apiKeys.map((apiKey) => {
                  const app = apps.find((a) => a.id === apiKey.app_id);
                  return (
                    <TableRow key={apiKey.id}>
                      <TableCell className="font-mono text-sm text-[var(--color-foreground)]">
                        {apiKey.key_prefix}...
                      </TableCell>
                      <TableCell className="text-sm text-[var(--color-foreground)]">
                        {app?.name || apiKey.app_id}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getPermissionBadgeVariant(apiKey.permissions)}>
                          {apiKey.permissions}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-[var(--color-muted-foreground)]">
                        {apiKey.rate_limit.toLocaleString()}/min
                      </TableCell>
                      <TableCell className="text-sm text-[var(--color-muted-foreground)]">
                        {formatDate(apiKey.created_at)}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteKeyId(apiKey.id)}
                          title="Delete API key"
                          className="text-[var(--color-destructive)] hover:bg-[var(--color-destructive)]/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Create API Key Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={(open) => !open && setShowCreateDialog(false)}>
          <DialogContent>
            <form onSubmit={handleCreate}>
              <DialogHeader>
                <DialogTitle>Create API Key</DialogTitle>
                <DialogDescription>
                  Generate a new API key for your application. The key will only be shown once after creation.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-[var(--color-foreground)]">
                    Application
                  </label>
                  <Select
                    value={newKeyAppId}
                    onChange={(e) => setNewKeyAppId(e.target.value)}
                    options={appOptions}
                    placeholder="Select an app"
                    className="w-full"
                    disabled={isCreating}
                  />
                  <p className="text-xs text-[var(--color-muted-foreground)]">
                    The app this API key will authenticate for.
                  </p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-[var(--color-foreground)]">
                    Permissions
                  </label>
                  <Select
                    value={newKeyPermissions}
                    onChange={(e) => setNewKeyPermissions(e.target.value as ApiKeyPermission)}
                    options={PERMISSION_OPTIONS}
                    className="w-full"
                    disabled={isCreating}
                  />
                  <p className="text-xs text-[var(--color-muted-foreground)]">
                    Write: send logs • Read: search logs • Admin: full access
                  </p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-[var(--color-foreground)]">
                    Rate Limit
                  </label>
                  <Input
                    type="number"
                    value={newKeyRateLimit}
                    onChange={(e) => setNewKeyRateLimit(e.target.value)}
                    placeholder="1000"
                    className="w-full"
                    min={1}
                    max={100000}
                    disabled={isCreating}
                  />
                  <p className="text-xs text-[var(--color-muted-foreground)]">
                    Maximum requests per minute (1 - 100,000).
                  </p>
                </div>
                {createError && (
                  <div className="flex items-center gap-2 rounded-lg border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 px-4 py-3 text-sm text-[var(--color-destructive)]">
                    {createError}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateDialog(false)}
                  disabled={isCreating}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isCreating || !newKeyAppId}>
                  {isCreating ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" />
                      Create Key
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Created Key Dialog */}
        <Dialog open={!!createdKey} onOpenChange={(open) => !open && setCreatedKey(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>API Key Created</DialogTitle>
              <DialogDescription>
                Your new API key has been created. Copy it now - you will not be able to see it again.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  value={createdKey?.key || ''}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCopyKey}
                  title="Copy to clipboard"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-[var(--color-success)]" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <div className="text-sm text-[var(--color-muted-foreground)]">
                <p><strong className="text-[var(--color-foreground)]">Permissions:</strong> {createdKey?.permissions}</p>
                <p><strong className="text-[var(--color-foreground)]">Rate Limit:</strong> {createdKey?.rate_limit.toLocaleString()}/min</p>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => setCreatedKey(null)}>Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!deleteKeyId} onOpenChange={(open) => !open && setDeleteKeyId(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete API Key</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete the API key <strong>{keyToDelete?.key_prefix}...</strong>?
                Any applications using this key will no longer be able to authenticate.
                This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDeleteKeyId(null)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

export default ApiKeyManager;
